declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
    gtag: (...args: unknown[]) => void
    google_tag_manager?: Record<string, unknown>
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] }
  }
}

/**
 * Same queue stub as Microsoft's own snippet, which keeps an existing `window.clarity`.
 * Clarity is loaded by GTM only after consent, so consent signals sent before it loads
 * must wait in this queue instead of being lost.
 */
function ensureClarityQueue() {
  if (window.clarity) return
  const stub = ((...args: unknown[]) => {
    (stub.q = stub.q || []).push(args)
  }) as NonNullable<Window['clarity']>
  window.clarity = stub
}

/** GTM container ids are of the form `GTM-XXXXXXX`; be permissive but reject anything
 * that could break out of the CSS attribute selector we build below. */
const SAFE_GTM_ID = /^[\w-]+$/

/**
 * True when the container is already present on the page.
 * Checks GTM's own runtime registry first (authoritative, survives script tag removal)
 * and falls back to looking for the injected <script> tag (covers the window between
 * appending the tag and the container finishing its boot).
 */
function isGtmAlreadyInitialized(gtmId: string): boolean {
  if (window.google_tag_manager?.[gtmId]) return true

  if (!SAFE_GTM_ID.test(gtmId)) return false

  try {
    return !!document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${gtmId}"]`)
  }
  catch {
    // Malformed selector - fall back to "not initialized" rather than crashing the plugin.
    return false
  }
}

export function pushToDataLayer(payload: Record<string, unknown>) {
  if (import.meta.server) return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

export function initGtmContainer(gtmId: string, siteEnv: string): boolean {
  if (import.meta.server) return false

  if (isGtmAlreadyInitialized(gtmId)) {
    return false
  }

  window.dataLayer = window.dataLayer || []
  // Must push the `arguments` object itself: gtag.js ignores a plain array, which would
  // silently drop the consent default below.
  window.gtag = window.gtag || function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments as unknown as Record<string, unknown>)
  }

  // Consent Mode v2: a `default` command carrying a `region` array applies ONLY to the
  // listed ISO 3166-2 regions; a command WITHOUT `region` is the global fallback for
  // everyone else. Since we deny by default everywhere (EEA-only Bulgarian business,
  // and denying is the safe posture for the rest of the world too), a single
  // region-less global default is both correct and sufficient. Adding a region list
  // here *instead of* the global default would leave non-EEA visitors with no default
  // at all, which Google treats as granted - the opposite of what we want.
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  })
  ensureClarityQueue()

  // The Google tag fires on gtm.js and picks its GA4 property from site_env, so site_env
  // must already be in the data model at that point.
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js', site_env: siteEnv })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
  script.onerror = () => {
    console.error('[GTM] Failed to load Google Tag Manager script')
  }
  script.onload = () => {
    console.log(`[GTM] Container ${gtmId} loaded successfully`)
  }
  document.head.appendChild(script)

  const noscript = document.createElement('noscript')
  const iframe = document.createElement('iframe')
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
  iframe.height = '0'
  iframe.width = '0'
  iframe.style.display = 'none'
  iframe.style.visibility = 'hidden'
  noscript.appendChild(iframe)
  document.body.insertBefore(noscript, document.body.firstChild)

  return true
}

/**
 * Last value actually pushed to Google Consent Mode in this page life.
 *
 * Deliberately module-scoped rather than local to a caller: the consent plugin, the
 * cookie banner and the privacy page all call `updateGoogleConsent` for the same state
 * change, and each needs to be free to call it eagerly (the privacy page in particular
 * must push the denial *synchronously*, before it clears Google's cookies). Keeping the
 * guard here makes the function idempotent for every caller instead of only for one.
 *
 * Resets on reload, which is correct - `gtag`/`dataLayer` are recreated too.
 */
let lastPushedConsent: boolean | null = null

/**
 * Update Google Consent Mode and Microsoft Clarity's consent based on the user's choice.
 * The single place where consent reaches the tags: the banner, the privacy page and a
 * withdrawal all go through here. Safe to call repeatedly - only a real change is pushed.
 */
export function updateGoogleConsent(granted: boolean) {
  if (import.meta.server) return
  if (!window.gtag) {
    // Not recorded: the container may still load later, and that first real push must
    // not be swallowed by a value we never actually delivered.
    console.warn('[GTM] Cannot update consent: gtag not initialized')
    return
  }

  if (lastPushedConsent === granted) return
  lastPushedConsent = granted

  const consentStatus = granted ? 'granted' : 'denied'

  window.gtag('consent', 'update', {
    analytics_storage: consentStatus,
    ad_storage: consentStatus,
    ad_user_data: consentStatus,
    ad_personalization: consentStatus,
    functionality_storage: consentStatus,
  })

  // Deleting Clarity's cookies does not stop a script that is already running.
  window.clarity?.('consentv2', { ad_Storage: consentStatus, analytics_Storage: consentStatus })
}
